import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import TabGroup from "@/components/ui/tab";
import CatalogCard from "@/components/ui/catalog-card";
import Table from "@/components/ui/table";
import Chip from "@/components/ui/chip";
import ProgressBar from "@/components/ui/progress-bar";
import FileImage from "@/components/ui/file-image";
import Dropdown from "@/components/ui/dropdown";
import { ArrowLeft, ArrowRight, PlayCircle } from "@/components/icons";
import { renderPaginationNumbers } from "@/utils/pagination";
import type { ViewMode, FilterField, SortOption, TableColumn } from "@/types/component";
import type { ClientEducation } from "../types/client-educations.types";
import { EnrollmentStatus } from "../types/client-education-detail.types";
import {
  clientEducationsApi,
  getButtonConfigForEducation,
} from "../services/client-educations.api";
import { educationCatalogApi } from "@/features/education-catalog/services/education-catalog.api";
import {
  uekEducationsApi,
  formatUekDate,
  getUekExamDisplay,
  type UekEducation,
} from "@/lib/uek-educations.api";
import styles from "./ClientEducationsPage.module.scss";

const PAGE_SIZE_OPTIONS = [12, 24, 48];
const UEK_TAB_INDEX = 2;

const ClientEducationsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [activeTab, setActiveTab] = useState(0); // 0 = Devam Eden, 1 = Tamamlanan, 2 = Uzaktan Eğitim Kapısı
  const [filterValues, setFilterValues] = useState<Record<string, unknown>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [ongoingEducations, setOngoingEducations] = useState<ClientEducation[]>([]);
  const [completedEducations, setCompletedEducations] = useState<ClientEducation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [uekEducations, setUekEducations] = useState<UekEducation[]>([]);
  const [uekTotal, setUekTotal] = useState(0);
  const [uekLoading, setUekLoading] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchEducations = useCallback(
    async (search?: string) => {
      setIsLoading(true);
      setError(null);
      try {
        const searchParam = search?.trim() || undefined;
        const [ongoingRes, completedRes] = await Promise.all([
          clientEducationsApi.getMyEducations({ isCompleted: false, search: searchParam }),
          clientEducationsApi.getMyEducations({ isCompleted: true, search: searchParam }),
        ]);
        // BE bazı durumlarda (ör. EXAM_FAILED — kullanıcı sınava tekrar
        // girebilirken içerik tamamlandı) aynı kaydı hem isCompleted=false
        // hem isCompleted=true cevabında döndürüyor. Aksiyon hâlâ
        // alınabildiği için kayıt "Devam Eden" sekmesinde kalmalı; aynı
        // id'yi "Tamamlanan" sekmesinden eliyoruz.
        const ongoingIds = new Set(ongoingRes.data.map((e) => e.id));
        setOngoingEducations(ongoingRes.data);
        setCompletedEducations(
          completedRes.data.filter((e) => !ongoingIds.has(e.id))
        );
      } catch (err: unknown) {
        const message =
          (err as { response?: { data?: { message?: string } }; message?: string })
            ?.response?.data?.message ||
          (err as { message?: string })?.message ||
          t("common.error");
        setError(message);
        setOngoingEducations([]);
        setCompletedEducations([]);
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    fetchEducations();
  }, [fetchEducations]);

  useEffect(() => {
    setCurrentPage(1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      fetchEducations(searchTerm);
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm, fetchEducations]);

  const isUekTab = activeTab === UEK_TAB_INDEX;

  // UEK kayıtları sunucu tarafında sayfalanır; yalnızca sekme açıkken istenir.
  useEffect(() => {
    if (!isUekTab) return;
    let cancelled = false;
    setUekLoading(true);
    uekEducationsApi
      .getMine({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch || undefined,
      })
      .then(({ data, total }) => {
        if (cancelled) return;
        setUekEducations(data);
        setUekTotal(total);
      })
      .catch(() => {
        if (cancelled) return;
        setUekEducations([]);
        setUekTotal(0);
      })
      .finally(() => {
        if (!cancelled) setUekLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isUekTab, currentPage, pageSize, debouncedSearch]);

  useEffect(() => {
    educationCatalogApi
      .getEducationCategories()
      .then((categories) => {
        setCategoryOptions(
          categories
            .map((c) => ({
              label: c.name ?? c.label ?? c.id ?? "",
              value: c.id ?? "",
            }))
            .filter((o) => o.label && o.value)
        );
      })
      .catch(() => setCategoryOptions([]));
  }, []);

  const tabs = [
    { label: t("clientEducations.tabs.ongoing"), content: null },
    { label: t("clientEducations.tabs.completed"), content: null },
    { label: t("uekEducations.tab"), content: null },
  ];

  const filterFields: FilterField[] = [
    {
      name: "category",
      label: t("clientEducations.filters.category"),
      type: "dropdown",
      placeholder: t("clientEducations.filters.categoryPlaceholder"),
      options: categoryOptions,
      searchable: true,
    },
    {
      name: "format",
      label: t("clientEducations.filters.format"),
      type: "dropdown",
      placeholder: t("clientEducations.filters.formatPlaceholder"),
      options: [
        { label: t("requestAndApproval.educationTypes.unit"), value: "2" },
        { label: t("requestAndApproval.educationTypes.faceToFace"), value: "0" },
        { label: t("requestAndApproval.educationTypes.online"), value: "1" },
        { label: t("requestAndApproval.educationTypes.nonUnit"), value: "3" },
      ],
    },
  ];

  const sortOptions: SortOption[] = [
    { label: t("clientEducations.sort.deadline"), value: "deadline" },
    { label: t("clientEducations.sort.progress"), value: "progress" },
    { label: t("clientEducations.sort.title"), value: "title" },
  ];

  const filteredByTab = useMemo(() => {
    return activeTab === 0 ? ongoingEducations : completedEducations;
  }, [activeTab, ongoingEducations, completedEducations]);

  const filteredItems = useMemo(() => {
    // Sekmeler backend'in `isCompleted` param'ına göre ayrı istekte doldurulur:
    //   - Devam Eden  → isCompleted=false  → ongoingEducations
    //   - Tamamlanan  → isCompleted=true   → completedEducations
    // Bu yüzden burada tab filtresi yapmıyoruz; sadece kategori/format filtreleri uygulanır.
    let list = filteredByTab;
    const categoryId = filterValues.category as string | undefined;
    if (categoryId) {
      list = list.filter((e) => e.categoryId === categoryId);
    }
    const formatVal = filterValues.format as string | undefined;
    if (formatVal !== undefined && formatVal !== "") {
      const typeNum = Number(formatVal);
      if (!Number.isNaN(typeNum)) {
        list = list.filter((e) => e.type === typeNum);
      }
    }
    return list;
  }, [filteredByTab, filterValues.category, filterValues.format]);

  const totalRecords = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, currentPage, pageSize]);

  const handleTabChange = (index: number) => {
    setActiveTab(index);
    setCurrentPage(1);
  };
  const handleFilterChange = (filters: Record<string, unknown>) => {
    setFilterValues(filters);
    setCurrentPage(1);
  };
  const handlePageChange = (page: number) => setCurrentPage(page);
  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };
  const handleSortChange = (option: SortOption) => {
    console.log("Sort:", option);
  };

  const handleCardClick = (id: string) => {
    navigate(`/dashboard/education-catalog/detail?id=${id}`);
  };

  const formatDurationLabel = (row: ClientEducation) => {
    const hours = row.durationHours ?? 0;
    const minutes = row.durationMinutes ?? 0;
    return `${hours} ${t("educationCatalog.durationDisplay.hoursShort")} ${minutes} ${t("educationCatalog.durationDisplay.minutesShort")}`;
  };

  const tableColumns: TableColumn<ClientEducation>[] = [
    {
      header: t("clientEducations.table.educationName"),
      accessor: (row) => (
        <div className={styles.tableEducationCell}>
          <div className={styles.tableThumbWrap}>
            <FileImage
              fileId={row.imageFileId}
              fallbackSrc={row.fallbackImageUrl ?? row.image}
              alt=""
              className={styles.tableThumb}
              objectFit="cover"
            />
          </div>
          <span className={styles.tableTitle}>{row.title}</span>
        </div>
      ),
    },
    {
      header: t("clientEducations.table.instructor"),
      accessor: (row) => row.educatorName,
    },
    {
      header: t("clientEducations.table.format"),
      accessor: (row) => (
        <Chip type="success" variant="filled" size="small">
          {row.deliveryType}
        </Chip>
      ),
    },
    {
      header: t("clientEducations.table.category"),
      accessor: (row) => (
        <Chip type="default" variant="outlined" size="small">
          {row.category}
        </Chip>
      ),
    },
    {
      header: t("clientEducations.table.requestDate"),
      accessor: (row) => row.requestedAt ?? "-",
    },
    {
      header: t("clientEducations.table.room"),
      accessor: (row) => row.roomInfo ?? "-",
    },
    {
      header: t("clientEducations.table.deadline"),
      accessor: (row) => row.deadline,
    },
    {
      header: t("clientEducations.table.duration"),
      accessor: (row) => formatDurationLabel(row),
    },
    {
      header: t("clientEducations.table.progress"),
      accessor: (row) =>
        row.enrollmentStatus === EnrollmentStatus.NOT_COMPLETED ? (
          <Chip type="error" variant="filled" size="small">
            {t("clientEducations.status.notCompleted")}
          </Chip>
        ) : (
          <div className={styles.progressCell}>
            <ProgressBar value={row.progress} showLabel size="small" />
            {row.hoursLeft && (
              <span className={styles.hoursLeft}>{row.hoursLeft}</span>
            )}
          </div>
        ),
    },
    {
      header: "",
      accessor: (row) => (
        <button
          type="button"
          className={styles.playButton}
          onClick={(e) => {
            e.stopPropagation();
            handleButtonAction(row);
          }}
          aria-label={t("clientEducations.play")}
        >
          <PlayCircle width={24} height={24} />
        </button>
      ),
    },
  ];

  const uekColumns: TableColumn<UekEducation>[] = [
    {
      header: t("uekEducations.cols.educationName"),
      accessor: (row) => <span className={styles.tableTitle}>{row.educationName}</span>,
    },
    {
      header: t("uekEducations.cols.format"),
      accessor: (row) => row.educationFormat ?? "—",
    },
    {
      header: t("uekEducations.cols.type"),
      accessor: (row) =>
        row.educationType ? (
          <Chip type="default" variant="outlined" size="small">
            {row.educationType}
          </Chip>
        ) : (
          "—"
        ),
    },
    {
      header: t("uekEducations.cols.completedAt"),
      accessor: (row) => formatUekDate(row.completedAt),
    },
    {
      header: t("uekEducations.cols.exam"),
      accessor: (row) => {
        const exam = getUekExamDisplay(row);
        return (
          <Chip type={exam.type} variant="filled" size="small">
            {exam.labelKey ? t(exam.labelKey) : exam.label}
          </Chip>
        );
      },
    },
  ];

  const handleButtonAction = (item: ClientEducation) => {
    const { buttonAction } = getButtonConfigForEducation(item);
    switch (buttonAction) {
      case "exam":
        navigate(`/dashboard/client-exams/take/${item.id}`);
        break;
      case "survey":
        navigate("/dashboard/client-polls", {
          state: {
            educationId: item.id,
            pollTitle: item.title,
          },
        });
        break;
      case "detail":
      default:
        navigate(`/dashboard/education-catalog/detail?id=${item.id}`);
    }
  };

  const catalogCardProps = (row: ClientEducation) => {
    const base = clientEducationsApi.mapToCatalogCardProps(row);
    const { buttonLabelKey, buttonDisabled } = getButtonConfigForEducation(row);
    return {
      ...base,
      duration: formatDurationLabel(row),
      // mapToCatalogCardProps statusText alanına i18n key koyabiliyor — burada çeviriyoruz.
      statusText: base.statusText ? t(base.statusText) : base.statusText,
      buttonLabel: t(buttonLabelKey),
      buttonDisabled,
      onButtonClick: () => handleButtonAction(row),
    };
  };

  return (
    <DashboardLayout>
      <Header title={t("clientEducations.title")} />

      <div className={styles.pageContainer}>
        <div className={styles.tabs}>
          <TabGroup
            tabs={tabs}
            defaultActiveTab={activeTab}
            onTabChange={handleTabChange}
            orientation="horizontal"
          />
        </div>

        {/* UEK sekmesi salt okunur bir tablo: kategori/format filtresi, sıralama ve kart görünümü yok. */}
        <PageHeader
          searchPlaceholder={t("clientEducations.searchPlaceholder")}
          searchValue={searchTerm}
          onSearch={setSearchTerm}
          filters={isUekTab ? [] : filterFields}
          onFilterChange={handleFilterChange}
          showViewToggle={!isUekTab}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          showSort={!isUekTab}
          sortOptions={sortOptions}
          onSort={handleSortChange}
        />

        {error && !isUekTab && (
          <div className={styles.errorMessage}>{error}</div>
        )}

        {isUekTab ? (
          <div className={styles.tableWrap}>
            <Table<UekEducation>
              columns={uekColumns}
              data={uekEducations}
              getRowId={(row) => row.id}
              isLoading={uekLoading}
              totalRecords={uekTotal}
              pageSize={pageSize}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              contentBasedHeight
              hasVisibleActions={false}
              renderActions={() => null}
              emptyMessage={t("uekEducations.noData")}
            />
          </div>
        ) : isLoading ? (
          <div className={styles.loadingState}>
            {t("common.loading")}
          </div>
        ) : viewMode === "grid" ? (
          <>
            <div className={`${styles.catalogGrid} ${styles[viewMode]}`}>
              {paginatedItems.map((item) => (
                <CatalogCard
                  key={item.id}
                  {...catalogCardProps(item)}
                  onCardClick={() => handleCardClick(item.id)}
                />
              ))}
            </div>
            {filteredItems.length === 0 && (
              <div className={styles.emptyState}>
                {t("clientEducations.noData")}
              </div>
            )}
            {filteredItems.length > 0 && (
              <div className={styles.paginationBar}>
                <div className={styles.paginationLeft}>
                  <span className={styles.paginationLabel}>
                    {t("pagination.show")}
                  </span>
                  <Dropdown
                    onSelect={(v) =>
                      handlePageSizeChange(Array.isArray(v) ? v[0] : v)
                    }
                    options={PAGE_SIZE_OPTIONS.map((size) => ({
                      label: size.toString(),
                      value: size.toString(),
                    }))}
                    menuPosition="top"
                    selectedLabel={pageSize.toString()}
                    value={pageSize.toString()}
                  />
                  <span className={styles.paginationDetails}>
                    {t("pagination.details", {
                      total: totalRecords,
                      start:
                        totalRecords === 0
                          ? 0
                          : (currentPage - 1) * pageSize + 1,
                      end: Math.min(currentPage * pageSize, totalRecords),
                    })}
                  </span>
                </div>
                <div className={styles.paginationControls}>
                  <button
                    type="button"
                    className={styles.paginationArrow}
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    <ArrowLeft height={16} width={16} />
                  </button>
                  {renderPaginationNumbers(
                    currentPage,
                    totalPages,
                    handlePageChange
                  )}
                  <button
                    type="button"
                    className={styles.paginationArrow}
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    <ArrowRight height={16} width={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className={styles.tableWrap}>
            <Table
              columns={tableColumns}
              data={paginatedItems}
              getRowId={(row) => row.id}
              onRowClick={(row) => handleCardClick(row.id)}
              totalRecords={totalRecords}
              pageSize={pageSize}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              contentBasedHeight
              hasVisibleActions={false}
              renderActions={() => null}
              emptyMessage={t("clientEducations.noData")}
            />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ClientEducationsPage;
